import type { FC, ReactEventHandler } from "react";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";
import LoadingOverlay from "@/components/LoadingOverlay";
import useTitle from "@/hooks/useTitle";
import {
  resumeExternalRouteSync,
  subscribeExternalIframeReset,
  suppressExternalRouteSync,
} from "@/routerConfig/externalWindowEventSync";
import { getExternalSrcPath } from "@/routerConfig/router.helpers";
import css from "./ExternalPage.module.css";

type ExternalPageProps = {
  title: string;
  path: string;
};

const ExternalPage: FC<ExternalPageProps> = ({ title, path }) => {
  useTitle(title);

  const [loaded, setLoaded] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const { pathname } = useLocation();
  // not including location search for now, see PR-2453
  const locationSrcPath = getExternalSrcPath(pathname || path);
  const [srcPath, setSrcPath] = useState(locationSrcPath);

  useEffect(() => {
    resumeExternalRouteSync();
    return () => suppressExternalRouteSync();
  }, []);

  useEffect(() => {
    setSrcPath(locationSrcPath);
  }, [locationSrcPath]);

  useEffect(() => {
    setLoaded(false);
  }, [srcPath, iframeKey]);

  useEffect(() => {
    return subscribeExternalIframeReset((targetPath) => {
      if (targetPath !== path) return;
      // Reload iframe to the nav item's canonical TB path (e.g. /home)
      setSrcPath(getExternalSrcPath(path));
      setIframeKey((key) => key + 1);
    });
  }, [path]);

  // Handle loading with an overlay that hides the ugly "Loading Nimbus..."
  // NOTE: loaded state reverts to false on HMR while the iframe content remains loaded
  const onLoad: ReactEventHandler<HTMLIFrameElement> = () => {
    setLoaded(true);
  };

  return (
    <div className={css.container}>
      {/*
        Trusted same-origin TB/microservice embed. Needs scripts and parent
        window access (handleRouteChange). Sandbox cannot include both
        allow-scripts and allow-same-origin without defeating itself.
      */}
      {/* oxlint-disable-next-line react/iframe-missing-sandbox */}
      <iframe
        key={iframeKey}
        className={css.iframe}
        title="main page content"
        src={srcPath}
        onLoad={onLoad}
      />
      <LoadingOverlay loaded={loaded} />
    </div>
  );
};

export default ExternalPage;
